import { subscribeMessageBodyCache } from "@/lib/cache/messageBodyCache";
import { queueWarmMessageBodies } from "@/lib/mail/bodyWarmup";
import {
  MAIL_BODY_WARMED_EVENT,
  type MailBodyWarmedDetail,
} from "@/lib/mail/mailBodyWarmEvents";
import { notePendingRevealActive } from "@/lib/mail/mailNotificationSoundGate";
import { getUnreadHold, setUnreadHoldCount } from "@/lib/mail/mailUnreadHold";
import { isClientRevealReady } from "@/lib/mail/messageBodyCacheStatus";

type PendingEntry = {
  accountId: number;
  folder: string;
  uid: number;
  unread: boolean;
  at: number;
};

const pending = new Map<string, PendingEntry>();
const listeners = new Set<() => void>();
let bodySubAttached = false;

/** Aligné sur le gate liste — hold/son levés dès mémoire ou DD02. */
const PENDING_MAX_MS = 45_000;

function entryKey(accountId: number, folder: string, uid: number): string {
  return `${accountId}\0${folder}\0${uid}`;
}

function notify(): void {
  listeners.forEach((cb) => cb());
  notePendingRevealActive(pending.size > 0);
}

function ensureBodySubscription(): void {
  if (bodySubAttached) return;
  bodySubAttached = true;
  subscribeMessageBodyCache(() => {
    sweepReadyPending();
  });
  window.addEventListener(MAIL_BODY_WARMED_EVENT, (event: Event) => {
    const detail = (event as CustomEvent<MailBodyWarmedDetail>).detail;
    if (!detail) return;
    sweepReadyPending();
  });
  window.setInterval(() => {
    sweepReadyPending();
    expireStalePending();
  }, 1_200);
}

function recountHoldForAccount(accountId: number): void {
  let unreadPending = 0;
  for (const entry of pending.values()) {
    if (entry.accountId === accountId && entry.unread) unreadPending += 1;
  }
  // Plancher pending uniquement — ne jamais descendre sous le hold preparing
  // (sinon badge = 4 alors que la liste masque encore les lignes).
  const current = getUnreadHold(accountId);
  setUnreadHoldCount(accountId, Math.max(unreadPending, current));
}

function removeEntry(key: string): void {
  const entry = pending.get(key);
  if (!entry) return;
  pending.delete(key);
  recountHoldForAccount(entry.accountId);
  notify();
}

function sweepReadyPending(): void {
  let changed = false;
  const touched = new Set<number>();
  for (const [key, entry] of [...pending.entries()]) {
    if (isClientRevealReady(entry.accountId, entry.folder, entry.uid)) {
      pending.delete(key);
      touched.add(entry.accountId);
      changed = true;
    }
  }
  for (const accountId of touched) {
    recountHoldForAccount(accountId);
  }
  if (changed) notify();
}

function expireStalePending(): void {
  const now = Date.now();
  const byFolder = new Map<string, { accountId: number; folder: string; uids: number[] }>();
  const touched = new Set<number>();

  for (const [key, entry] of [...pending.entries()]) {
    if (now - entry.at < PENDING_MAX_MS) continue;
    pending.delete(key);
    touched.add(entry.accountId);
    const folderKey = `${entry.accountId}\0${entry.folder}`;
    const group = byFolder.get(folderKey) ?? {
      accountId: entry.accountId,
      folder: entry.folder,
      uids: [],
    };
    group.uids.push(entry.uid);
    byFolder.set(folderKey, group);
  }

  for (const group of byFolder.values()) {
    queueWarmMessageBodies(group.accountId, group.folder, group.uids, { newMail: true });
  }
  for (const accountId of touched) {
    recountHoldForAccount(accountId);
  }
  if (touched.size > 0) notify();
}

export function subscribeMailPendingReveal(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isMailPendingReveal(
  accountId: number,
  folder: string,
  uid: number
): boolean {
  if (uid <= 0) return false;
  return pending.has(entryKey(accountId, folder, uid));
}

export function getMailPendingRevealCount(): number {
  return pending.size;
}

export function countMailPendingRevealForAccount(accountId: number): number {
  let n = 0;
  for (const entry of pending.values()) {
    if (entry.accountId === accountId) n += 1;
  }
  return n;
}

export function countUnreadMailPendingRevealForAccount(accountId: number): number {
  let n = 0;
  for (const entry of pending.values()) {
    if (entry.accountId === accountId && entry.unread) n += 1;
  }
  return n;
}

/**
 * UIDs à ne révéler / notifier qu’une fois le corps en **mémoire** (open instantané).
 */
export function markMailPendingReveal(
  accountId: number,
  folder: string,
  uids: number[],
  options?: { unreadUids?: number[] }
): void {
  if (accountId < 1 || uids.length === 0) return;
  ensureBodySubscription();

  const unreadSet = new Set(options?.unreadUids ?? []);
  const now = Date.now();
  let added = 0;
  const toWarm: number[] = [];

  for (const uid of uids) {
    if (uid <= 0) continue;
    if (isClientRevealReady(accountId, folder, uid)) continue;
    const key = entryKey(accountId, folder, uid);
    if (pending.has(key)) continue;
    pending.set(key, {
      accountId,
      folder,
      uid,
      unread: unreadSet.has(uid),
      at: now,
    });
    toWarm.push(uid);
    added += 1;
  }

  if (toWarm.length > 0) {
    queueWarmMessageBodies(accountId, folder, toWarm, { newMail: true });
  }

  if (added > 0) {
    recountHoldForAccount(accountId);
    notify();
  }
}

export function clearMailPendingReveal(
  accountId: number,
  folder: string,
  uid: number
): void {
  removeEntry(entryKey(accountId, folder, uid));
}
